import { useEffect, useRef, useState } from "react";

const HERO_BG =
  "https://storage.helloreaddy.io/project_files/3695ba64-190a-4f8e-a9e2-04b761fa00c1/7049352d-e690-4110-bc07-06a644353898_compressed_aj-landing-2.webp";

export default function Hero() {
  const [scrollY, setScrollY] = useState(0);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Trigger entrance animation after mount
    const timer = setTimeout(() => setVisible(true), 100);

    const handleScroll = () => {
      if (sectionRef.current) {
        const rect = sectionRef.current.getBoundingClientRect();
        // Only compute parallax while section is in view
        if (rect.bottom > 0 && rect.top < window.innerHeight) {
          setScrollY(window.scrollY);
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      clearTimeout(timer);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const parallaxOffset = scrollY * 0.3;

  const fadeUp = (delay: number) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.8s ease-out ${delay}s, transform 0.8s ease-out ${delay}s`,
  });

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-[520px] md:min-h-[680px] lg:min-h-[720px] flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with parallax */}
      <div
        className="absolute inset-0 w-full h-[120%]"
        style={{ transform: `translateY(${parallaxOffset}px)` }}
      >
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover"
        />
        {/* Overlay for text contrast */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />
      </div>

      {/* Content with staggered fade-in */}
      <div className="relative z-10 w-full max-w-5xl mx-auto text-center px-4 md:px-8 py-12">
        <h1
          className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-extrabold text-white tracking-tight leading-tight"
          style={fadeUp(0)}
        >
          Amin &amp; Jahan Corporation Ltd.
        </h1>

        <p
          className="mt-4 md:mt-6 text-lg md:text-xl lg:text-2xl font-heading font-semibold text-white/90 tracking-wide"
          style={fadeUp(0.15)}
        >
          Research. Develop. Promote.
        </p>

        <p
          className="mt-4 md:mt-6 text-base md:text-lg text-white/80 max-w-3xl mx-auto leading-relaxed"
          style={fadeUp(0.3)}
        >
          An integrated research, development &amp; promotion service provider
          connecting knowledge, capability, industry and innovation.
        </p>

        <div
          className="mt-8 md:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          style={fadeUp(0.45)}
        >
          <button
            type="button"
            onClick={() => scrollTo("#what-we-do")}
            className="px-7 py-3 text-sm font-semibold bg-primary-500 text-white rounded-md hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Explore What We Do
          </button>
          <button
            type="button"
            onClick={() => scrollTo("#legacy")}
            className="px-7 py-3 text-sm font-semibold bg-white/10 text-white border border-white/30 rounded-md hover:bg-white/20 transition-colors whitespace-nowrap"
          >
            Our Legacy
          </button>
        </div>
      </div>
    </section>
  );
}